/**
 * The center create/edit form — the panel's reason to exist: add and manage
 * centers without hand-editing centers.yml.
 *
 * One form serves both modes. Create starts from the doctor, not the domain:
 * the domain IS the doctor id (Contract A), so the operator names the doctor by
 * their Health-360 login and the form previews what Save will do — verify an
 * existing account, or register a new one — before anything is claimed. The
 * domain is then derived, never typed; a typed one could point a center at
 * somebody else's id. In edit mode the identity is fixed and shown read-only —
 * changing a port is a migration, and changing the domain would re-point the
 * center at another doctor's clinic.
 *
 * The doctor's password passes through here to the controller and on to the
 * gateway. It is never prefilled, the confirm copy never leaves the browser,
 * and both are dropped from state the moment the submit returns.
 *
 * The per-field help text is deliberate: it is the documentation that used to
 * live in centers.yml's comments, which the move to a database would otherwise
 * have lost. This is its new home.
 *
 * Nothing here applies anything. Saving writes desired state to the database;
 * the operator then previews and applies on the Plan page. The form says so, so
 * "saved" is never mistaken for "live".
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from './ui/Button';
import { Card } from './ui/Card';
import {
  Checkbox,
  NumberInput,
  Select,
  SelectItem,
  TextInput,
} from './ui/Form';
import { AddIcon, TrashIcon } from './ui/Icon';

import { ApiError, apiFetch, apiPost, apiPut, describeError } from '../lib/api';
import { pluralise } from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  CenterAllocationResponse,
  CenterConfigFields,
  CenterCreateRequest,
  CenterCreateResponse,
  CenterWriteRequest,
  CenterWriteResponse,
  DateRestrictionInput,
  DoctorLookupResponse,
  LabelRestrictionInput,
  ModalityInput,
  PeerInput,
} from '../types/api';
import { Badge } from './Badge';
import { Callout } from './Callout';

export interface CenterFormValue {
  name: string;
  /** Edit mode only: round-tripped unchanged. Create derives it server-side. */
  domain: string;
  region: string;
  cache_ram_mb: number;
  dicom_always_allow_find: boolean;
  dicom_always_allow_get: boolean;
  dicom_always_allow_move: boolean;
  modalities: ModalityInput[];
  date_restrictions: DateRestrictionInput[];
  label_restrictions: LabelRestrictionInput[];
  peers: PeerInput[];
}

export const EMPTY_CENTER: CenterFormValue = {
  name: '',
  domain: '',
  region: '',
  cache_ram_mb: 8428,
  dicom_always_allow_find: false,
  dicom_always_allow_get: false,
  dicom_always_allow_move: false,
  modalities: [],
  date_restrictions: [],
  label_restrictions: [],
  peers: [],
};

function toConfig(v: CenterFormValue, provisionDatabase: boolean): CenterConfigFields {
  return {
    name: v.name.trim() || null,
    region: v.region.trim(),
    cache_ram_mb: v.cache_ram_mb,
    dicom_always_allow_find: v.dicom_always_allow_find,
    dicom_always_allow_get: v.dicom_always_allow_get,
    dicom_always_allow_move: v.dicom_always_allow_move,
    modalities: v.modalities,
    date_restrictions: v.date_restrictions,
    label_restrictions: v.label_restrictions,
    peers: v.peers,
    provision_database: provisionDatabase,
  };
}

// ── the doctor account (create mode) ─────────────────────────────────────────

interface DoctorDraft {
  email: string;
  password: string;
  confirm: string;
  name: string;
  /** null until the operator picks a date; until then it follows the lookup. */
  expireDate: string | null;
}

const EMPTY_DOCTOR: DoctorDraft = {
  email: '',
  password: '',
  confirm: '',
  name: '',
  expireDate: null,
};

/** The gateway's register call caps the name at its workspace-name limit. */
const DOCTOR_NAME_MAX = 150;

/** Loose on purpose: it only decides when a lookup is worth sending. The
 *  server matches exactly and is the judge of what an address is. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Clinic360-web's sign-up rule (registrationSchema.js), which the server
 *  enforces again before registering: 8+ letters and digits, with at least one
 *  upper-case letter, one lower-case letter and one digit. */
function meetsSignupRule(password: string): boolean {
  return (
    /^[A-Za-z0-9]{8,}$/.test(password) &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password)
  );
}

/** YYYY-MM-DD on the operator's own calendar. `toISOString` is UTC, and would
 *  name yesterday for anyone east of Greenwich shortly after midnight. */
function localDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function daysFromToday(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDay(date);
}

function oneYearFromToday(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return localDay(date);
}

/** A new account gets a year. An existing one keeps the end it already has
 *  when that is comfortably ahead — creating a center must not quietly shorten
 *  a subscription — and otherwise gets a year from today. */
function defaultExpiry(found: DoctorLookupResponse | null): string {
  const current = found?.exists ? found.expire_date : null;
  if (current && current > daysFromToday(30)) return current;
  return oneYearFromToday();
}

type Lookup =
  | { state: 'idle' }
  | { state: 'pending' }
  | { state: 'failed'; error: string }
  | { state: 'done'; result: DoctorLookupResponse };

type LookupOutcome =
  | { email: string; result: DoctorLookupResponse }
  | { email: string; error: string };

/**
 * The live preview behind the email field: debounced, so typing an address
 * sends one request rather than one per keystroke, and keyed on the email the
 * way the server matches it (strip + lower), so changing only the case does
 * not re-ask.
 *
 * Only outcomes are stored. "Pending" is derived — an outcome filed under a
 * different email than the one in the field is stale — so a slow answer for
 * an earlier email can never be shown as the answer for the current one.
 */
function useDoctorLookup(email: string, enabled: boolean): [Lookup, () => void] {
  const key = email.trim().toLowerCase();
  const wanted = enabled && EMAIL_SHAPE.test(key);
  const [outcome, setOutcome] = useState<LookupOutcome | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!wanted) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await apiFetch<DoctorLookupResponse>(
          `/api/centers/doctor-lookup?email=${encodeURIComponent(key)}`,
          { signal: controller.signal },
        );
        if (!controller.signal.aborted) setOutcome({ email: key, result });
      } catch (cause) {
        if (!controller.signal.aborted) {
          setOutcome({ email: key, error: describeError(cause) });
        }
      }
    }, 350);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [wanted, key, attempt]);

  const retry = () => {
    setOutcome(null);
    setAttempt((n) => n + 1);
  };

  if (!wanted) return [{ state: 'idle' }, retry];
  if (!outcome || outcome.email !== key) return [{ state: 'pending' }, retry];
  if ('error' in outcome) return [{ state: 'failed', error: outcome.error }, retry];
  return [{ state: 'done', result: outcome.result }, retry];
}

/**
 * Why Save is refused right now, in the operator's words — or null when it is
 * not. The server re-checks every one of these; doing it here spares a round
 * trip, and spares typing a password into a create that could never succeed.
 * Only the first reason is named, because the verdict decides what comes next
 * (a new account needs a name and a confirmed password; an existing one not).
 */
function createBlocker(
  unavailable: string | null,
  doctor: DoctorDraft,
  lookup: Lookup,
  expireDate: string,
  today: string,
): string | null {
  if (unavailable) return 'Centers cannot be created on this deployment — see the top of the form.';
  if (!doctor.email.trim()) return "Enter the doctor's Health-360 email.";
  if (lookup.state === 'idle') return 'That does not look like an email address yet.';
  if (lookup.state === 'pending') return 'Checking the email…';
  if (lookup.state === 'failed') return 'The email could not be checked — see the Doctor account card.';
  const found = lookup.result;
  if (found.blocks.length > 0) return 'This email cannot be used — see the Doctor account card.';
  if (found.exists && found.center_slug) {
    return `Doctor #${found.doctor_id} already has a center (${found.center_slug}).`;
  }
  if (!found.exists && !found.can_register) return 'Registering new accounts is switched off.';
  if (!doctor.password) return "Enter the doctor's password.";
  if (!found.exists) {
    if (!meetsSignupRule(doctor.password)) return "The password does not meet Clinic360's sign-up rule.";
    if (doctor.confirm !== doctor.password) return 'The two passwords do not match.';
    if (!doctor.name.trim()) return "Enter the doctor's name.";
  }
  if (!expireDate || expireDate <= today) return 'The subscription must end after today.';
  return null;
}

// ── the form ─────────────────────────────────────────────────────────────────

type CenterFormProps =
  | {
      mode: 'create';
      initial: CenterFormValue;
      onCreated: (response: CenterCreateResponse) => void;
    }
  | {
      mode: 'edit';
      slug: string;
      initial: CenterFormValue;
      onSaved: (response: CenterWriteResponse) => void;
    };

export function CenterForm(props: CenterFormProps) {
  const creating = props.mode === 'create';
  const [v, setV] = useState<CenterFormValue>(props.initial);
  const [doctor, setDoctor] = useState<DoctorDraft>(EMPTY_DOCTOR);
  const [provisionDatabase, setProvisionDatabase] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // Fetched once on mount, not per keystroke: whether a create can work here
  // at all must be on screen before anyone types a password.
  const alloc = useResource<CenterAllocationResponse>('/api/centers/allocation', {
    enabled: creating,
  });
  // Both create endpoints are owner-only: a 403 here means this user can never
  // create a center, which must read as a plain refusal, not a load failure.
  const forbidden = alloc.error instanceof ApiError && alloc.error.status === 403;
  const unavailable = forbidden
    ? 'Only an owner can create centers.'
    : alloc.data?.create_unavailable_reason ?? null;
  const apex = alloc.data?.domain_apex ?? null;

  const [lookup, recheck] = useDoctorLookup(doctor.email, creating);
  const found = lookup.state === 'done' ? lookup.result : null;
  const expireDate = doctor.expireDate ?? defaultExpiry(found);
  const today = localDay(new Date());
  const blocker = creating ? createBlocker(unavailable, doctor, lookup, expireDate, today) : null;

  // Save sits at the foot of a long form and the error at its head.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'center' });
  }, [error]);

  function patch(p: Partial<CenterFormValue>) {
    setV((prev) => ({ ...prev, ...p }));
  }

  function patchDoctor(p: Partial<DoctorDraft>) {
    setDoctor((prev) => ({ ...prev, ...p }));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (props.mode === 'create') {
        const body: CenterCreateRequest = {
          ...toConfig(v, provisionDatabase),
          doctor: {
            email: doctor.email.trim(),
            password: doctor.password,
            name: found && !found.exists ? doctor.name.trim() : null,
            expire_date: expireDate,
          },
        };
        props.onCreated(await apiPost<CenterCreateResponse>('/api/centers', body));
      } else {
        const body: CenterWriteRequest = { ...toConfig(v, provisionDatabase), domain: v.domain };
        props.onSaved(
          await apiPut<CenterWriteResponse>(
            `/api/centers/${encodeURIComponent(props.slug)}/config`,
            body,
          ),
        );
      }
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      // Success or not, the password leaves state now. A retry re-types it,
      // which beats it sitting in memory for as long as the tab stays open.
      setDoctor((prev) => ({ ...prev, password: '', confirm: '' }));
      setBusy(false);
    }
  }

  const domain = creating
    ? domainPreview(found, apex)
    : {
        value: v.domain,
        placeholder: '',
        help: 'Fixed. Its first label is the doctor id in the shared clinical database (Contract A); a center for a different doctor is a new center.',
      };

  return (
    <div className="stack">
      {error ? (
        <div ref={errorRef}>
          <Callout tone="critical" title="Could not save">
            {error}
          </Callout>
        </div>
      ) : null}

      {creating && unavailable ? (
        <Callout tone="critical" title="Centers cannot be created on this deployment" live>
          {unavailable}
        </Callout>
      ) : null}

      {creating ? (
        <DoctorAccountCard
          doctor={doctor}
          onChange={patchDoctor}
          lookup={lookup}
          onRecheck={recheck}
          expireDate={expireDate}
          today={today}
          registrationEnabled={alloc.data?.registration_enabled ?? null}
          disabled={unavailable !== null || busy}
        />
      ) : null}

      <Card className="stack">
        <h2 className="section__title">Identity</h2>
        <TextInput
          id="center-domain"
          labelText="Domain"
          helperText={domain.help}
          value={domain.value}
          placeholder={domain.placeholder}
          readOnly
        />

        <TextInput
          id="center-region"
          labelText="Region"
          helperText="An operational grouping — a data-centre, a country, a customer region. Metadata only: it groups centers in this console and is rendered into no config file, so changing it can never produce a plan diff. Leave blank for ungrouped."
          value={v.region}
          onChange={(e) => patch({ region: e.target.value })}
          placeholder="eu-west-1"
        />

        <TextInput
          id="center-name"
          labelText="Display name"
          helperText="Shown in the viewer and Orthanc UI. Optional — falls back to the slug."
          value={v.name}
          onChange={(e) => patch({ name: e.target.value })}
        />

        {creating ? (
          <div className="alloc">
            <h3 className="section__subtitle">Auto-assigned</h3>
            {alloc.data ? (
              <dl className="kv">
                <dt>Slug</dt>
                <dd><code>{alloc.data.slug}</code></dd>
                <dt>Web port</dt>
                <dd><code>{alloc.data.web_port}</code> <span className="muted">(loopback)</span></dd>
                <dt>DICOM port</dt>
                <dd><code>{alloc.data.dicom_port}</code> <span className="muted">(public)</span></dd>
                <dt>Database</dt>
                <dd><code>{alloc.data.database_name}</code></dd>
                <dt>S3 prefix</dt>
                <dd><code>{alloc.data.s3_prefix}</code></dd>
              </dl>
            ) : alloc.error ? (
              <p className="muted">
                Could not load the allocation preview: {describeError(alloc.error)}{' '}
                <Button kind="ghost" size="sm" onClick={alloc.reload}>
                  Retry
                </Button>
              </p>
            ) : (
              <p className="muted">Loading the slug, ports and database this center would get…</p>
            )}
          </div>
        ) : null}
      </Card>

      <Card className="stack">
        <h2 className="section__title">Runtime</h2>
        <NumberInput
          id="center-cache-ram"
          label="Cache RAM (MB)"
          helperText="Orthanc's MaximumStorageCacheSize. The VM's tuning profile budgets ~12 GB total across all centers — sum these deliberately."
          min={1}
          value={v.cache_ram_mb}
          onChange={(_e, { value }: { value: number | string }) =>
            patch({ cache_ram_mb: Number(value) })
          }
        />
        <div className="stack">
          <span className="field__label">DICOM allow-any (dangerous)</span>
          <p className="field__help muted">
            Let <em>unregistered</em> AE titles issue the operation. Convenient for a new
            scanner, but it lets any device on the network query or pull this center&rsquo;s
            studies. Leave off unless you know why.
          </p>
          {(['find', 'get', 'move'] as const).map((op) => {
            const key = `dicom_always_allow_${op}` as const;
            return (
              <Checkbox
                key={op}
                id={`dicom-allow-${op}`}
                labelText={`C-${op.toUpperCase()}`}
                checked={v[key]}
                onChange={(_e, { checked }: { checked: boolean }) =>
                  patch({ [key]: checked } as Partial<CenterFormValue>)
                }
              />
            );
          })}
        </div>
      </Card>

      <ModalityEditor
        value={v.modalities}
        onChange={(modalities) => patch({ modalities })}
      />
      <RestrictionEditor
        dates={v.date_restrictions}
        labels={v.label_restrictions}
        onDates={(date_restrictions) => patch({ date_restrictions })}
        onLabels={(label_restrictions) => patch({ label_restrictions })}
      />
      <PeerEditor value={v.peers} onChange={(peers) => patch({ peers })} />

      <Card className="stack">
        {creating ? (
          <Checkbox
            id="provision-database"
            labelText="Also create the Postgres database now"
            checked={provisionDatabase}
            onChange={(_e, { checked }: { checked: boolean }) => setProvisionDatabase(checked)}
          />
        ) : null}
        <Callout tone="info" title="Saving writes desired state, not live config">
          This stores the center in the database. Nothing on the fleet changes until you
          preview and apply on the Plan page.
        </Callout>
        <div>
          <Button
            onClick={save}
            disabled={busy || blocker !== null}
            aria-describedby={blocker ? 'center-save-blocker' : undefined}
          >
            {busy ? 'Saving…' : creating ? 'Create center' : 'Save changes'}
          </Button>
        </div>
        {blocker && !busy ? (
          <p className="field__help muted" id="center-save-blocker">
            {blocker}
          </p>
        ) : null}
      </Card>
    </div>
  );
}

/** What the read-only Domain field shows while a center is being created. */
function domainPreview(
  found: DoctorLookupResponse | null,
  apex: string | null,
): { value: string; placeholder: string; help: string } {
  const zone = apex ?? '<zone>';
  if (found?.exists && found.doctor_id !== null) {
    return {
      value: `${found.doctor_id}.${zone}`,
      placeholder: '',
      help: "The doctor's id is its first label (Contract A) — the whole system routes on it.",
    };
  }
  if (found && !found.exists && found.can_register) {
    return {
      value: `<new id>.${zone}`,
      placeholder: '',
      help: 'Assigned when the account is created: the id the gateway issues becomes the first label.',
    };
  }
  return {
    value: '',
    placeholder: `<doctor id>.${zone}`,
    help: 'Derived from the doctor account, never typed: its doctor id is the first label (Contract A).',
  };
}

function DoctorAccountCard({
  doctor,
  onChange,
  lookup,
  onRecheck,
  expireDate,
  today,
  registrationEnabled,
  disabled,
}: {
  doctor: DoctorDraft;
  onChange: (p: Partial<DoctorDraft>) => void;
  lookup: Lookup;
  onRecheck: () => void;
  expireDate: string;
  today: string;
  /** null until the allocation preview has loaded. */
  registrationEnabled: boolean | null;
  disabled: boolean;
}) {
  const found = lookup.state === 'done' ? lookup.result : null;
  const existing = found?.exists ? found : null;
  const registering = found !== null && !found.exists && found.can_register;

  const weakPassword = registering && doctor.password !== '' && !meetsSignupRule(doctor.password);
  const mismatch = registering && doctor.confirm !== '' && doctor.confirm !== doctor.password;
  const expiryInvalid = !expireDate || expireDate <= today;
  const shortens =
    existing?.expire_date != null && expireDate !== '' && expireDate < existing.expire_date;

  return (
    <Card className="stack">
      <h2 className="section__title">Doctor account</h2>
      <p className="field__help muted">
        The doctor this center belongs to, named by their Health-360 login — the email and
        password they sign in to Clinic360 with. Save checks it through the Health-360
        gateway, or registers it when no account uses the email; this panel never stores it.
        {registrationEnabled === false
          ? ' Registering new accounts is switched off here, so the email must already belong to a doctor.'
          : null}
      </p>

      {/* Not type="email", and a name of its own: a password manager seeing an
          email field beside a password field offers the operator's own saved
          panel login, and one accepted suggestion sends the wrong account. */}
      <TextInput
        id="doctor-email"
        name="doctor-email"
        labelText="Email"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder="doctor@example.com"
        value={doctor.email}
        onChange={(e) => onChange({ email: e.target.value })}
        disabled={disabled}
      />

      {/* Always mounted, so a verdict that appears is announced. */}
      <div className="centers-lookup stack" aria-live="polite">
        <LookupVerdict lookup={lookup} onRecheck={onRecheck} />
      </div>

      <TextInput
        id="doctor-password"
        name="doctor-password"
        type="password"
        autoComplete="new-password"
        labelText="Password"
        helperText={
          registering
            ? "Becomes their Health-360 password. At least 8 letters and digits, including an upper-case letter, a lower-case letter and a digit — Clinic360's sign-up rule."
            : existing
              ? 'Their current Health-360 password. Save verifies it through the gateway; a wrong one creates nothing.'
              : 'Their Health-360 password — checked through the gateway on Save, never stored.'
        }
        invalid={weakPassword}
        invalidText="Does not meet Clinic360's sign-up rule."
        value={doctor.password}
        onChange={(e) => onChange({ password: e.target.value })}
        disabled={disabled}
      />

      {registering ? (
        <TextInput
          id="doctor-password-confirm"
          name="doctor-password-confirm"
          type="password"
          autoComplete="new-password"
          labelText="Confirm password"
          helperText="A typo here would leave a new account nobody can sign in to. Compared in this browser only; never sent."
          invalid={mismatch}
          invalidText="Does not match the password."
          value={doctor.confirm}
          onChange={(e) => onChange({ confirm: e.target.value })}
          disabled={disabled}
        />
      ) : null}

      {registering ? (
        <TextInput
          id="doctor-name"
          name="doctor-name"
          labelText="Doctor name"
          helperText={`Their name on Health-360; the gateway also names their workspace after it. Up to ${DOCTOR_NAME_MAX} characters.`}
          autoComplete="off"
          maxLength={DOCTOR_NAME_MAX}
          dir="auto"
          value={doctor.name}
          onChange={(e) => onChange({ name: e.target.value })}
          disabled={disabled}
        />
      ) : null}

      <TextInput
        id="doctor-expire"
        type="date"
        labelText="Subscription expires"
        min={daysFromToday(1)}
        helperText={
          shortens
            ? `Earlier than their current end (${existing?.expire_date}) — saving shortens their subscription.`
            : existing
              ? `Their subscription currently ends ${existing.expire_date ?? 'on an unreadable date'}. Written to their record only when this differs.`
              : 'Defaults to a year from today.'
        }
        invalid={expiryInvalid}
        invalidText="Pick a date after today."
        value={expireDate}
        onChange={(e) => onChange({ expireDate: e.target.value })}
        disabled={disabled}
      />
    </Card>
  );
}

/** One verdict on the typed email — exactly one of these at a time. */
function LookupVerdict({ lookup, onRecheck }: { lookup: Lookup; onRecheck: () => void }) {
  if (lookup.state === 'idle') return null;
  if (lookup.state === 'pending') return <p className="muted">Checking this email…</p>;
  if (lookup.state === 'failed') {
    return (
      <Callout tone="critical" title="Could not check this email">
        <p>{lookup.error}</p>
        <Button kind="tertiary" size="sm" onClick={onRecheck}>
          Check again
        </Button>
      </Callout>
    );
  }

  const found = lookup.result;
  const warnings =
    found.warnings.length > 0 ? (
      <Callout
        tone="warning"
        title={`${found.warnings.length} ${pluralise(found.warnings.length, 'thing')} to know before saving`}
      >
        <ul className="reasons">
          {found.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      </Callout>
    ) : null;

  if (found.blocks.length > 0) {
    return (
      <>
        <Callout tone="critical" title="This email cannot be used">
          <ul className="reasons">
            {found.blocks.map((block) => (
              <li key={block}>{block}</li>
            ))}
          </ul>
        </Callout>
        {warnings}
      </>
    );
  }

  if (found.exists) {
    if (found.center_slug) {
      return (
        <Callout tone="critical" title={`Doctor #${found.doctor_id} already has a center`}>
          <Link to={`/centers/${encodeURIComponent(found.center_slug)}`}>{found.center_slug}</Link>{' '}
          routes to this doctor id, and one doctor id serves exactly one center.
        </Callout>
      );
    }
    return (
      <>
        <Callout
          tone="good"
          title={
            <>
              Existing doctor #{found.doctor_id} · <bdi>{found.name ?? 'unnamed'}</bdi> ·
              subscription until {found.expire_date ?? 'unknown'}
            </>
          }
        >
          Save verifies their password through the gateway; no new account is created.
        </Callout>
        {warnings}
      </>
    );
  }

  if (!found.can_register) {
    return (
      <Callout tone="critical" title="Registration is switched off">
        No Health-360 doctor account uses this email, and registering new ones is disabled on
        this deployment (<code>FLEET_DOCTOR_REGISTRATION_ENABLED</code>). Use an existing
        doctor&rsquo;s login.
      </Callout>
    );
  }

  return (
    <>
      <Callout tone="info" title="New Health-360 account will be registered">
        They will receive Health-360 welcome emails. The account is permanent — this panel
        cannot remove it.
      </Callout>
      {warnings}
    </>
  );
}

// ── modalities ───────────────────────────────────────────────────────────────

function ModalityEditor({
  value,
  onChange,
}: {
  value: ModalityInput[];
  onChange: (m: ModalityInput[]) => void;
}) {
  function update(i: number, patch: Partial<ModalityInput>) {
    onChange(value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  return (
    <Card className="stack">
      <div className="toolbar">
        <h2 className="section__title">DICOM modalities <Badge tone="neutral">{value.length}</Badge></h2>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={AddIcon}
          onClick={() => onChange([...value, { symbolic_name: '', aet: '', host: '*', port: 104 }])}
        >
          Add modality
        </Button>
      </div>
      <p className="field__help muted">
        Devices this center will talk to (AE title, host, port). The symbolic name and AE
        title are conventionally identical; a mismatch is a real, flaggable data bug.
      </p>
      {value.map((m, i) => (
        <div key={i} className="row-editor">
          <TextInput id={`modality-${i}-symbolic`} labelText="Symbolic name" hideLabel
            placeholder="Symbolic name" value={m.symbolic_name}
            onChange={(e) => update(i, { symbolic_name: e.target.value })} />
          <TextInput id={`modality-${i}-aet`} labelText="AE title" hideLabel
            placeholder="AE title" value={m.aet}
            onChange={(e) => update(i, { aet: e.target.value })} />
          <TextInput id={`modality-${i}-host`} labelText="Host (* = any)" hideLabel
            placeholder="Host (* = any)" value={m.host}
            onChange={(e) => update(i, { host: e.target.value })} />
          <NumberInput id={`modality-${i}-port`} label="Port" hideLabel value={m.port}
            onChange={(_e, { value }: { value: number | string }) => update(i, { port: Number(value) })} />
          <Button kind="ghost" size="sm" renderIcon={TrashIcon} hasIconOnly iconDescription="Remove"
            onClick={() => onChange(value.filter((_, idx) => idx !== i))} />
        </div>
      ))}
    </Card>
  );
}

// ── restrictions ─────────────────────────────────────────────────────────────

// Exported: an edge node's cfind_filters use exactly these two shapes, so the
// node wizard renders the same editor rather than a divergent copy of it.
export function RestrictionEditor({
  dates,
  labels,
  onDates,
  onLabels,
}: {
  dates: DateRestrictionInput[];
  labels: LabelRestrictionInput[];
  onDates: (d: DateRestrictionInput[]) => void;
  onLabels: (l: LabelRestrictionInput[]) => void;
}) {
  function updateDate(i: number, patch: Partial<DateRestrictionInput>) {
    onDates(dates.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }
  function updateLabel(i: number, patch: Partial<LabelRestrictionInput>) {
    onLabels(labels.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  return (
    <Card className="stack">
      <h2 className="section__title">C-FIND restrictions</h2>
      <p className="field__help muted">
        Limit what a calling AE title may query, by study date or by Orthanc label. An AE
        title named here must match a declared modality above, or the rule never fires.
      </p>

      <div className="toolbar">
        <h3 className="section__subtitle">By date</h3>
        <Button kind="tertiary" size="sm" renderIcon={AddIcon}
          onClick={() => onDates([...dates, { aet: '', mode: 'today', days: null, start_date: null, end_date: null }])}>
          Add
        </Button>
      </div>
      {dates.map((d, i) => (
        <div key={i} className="row-editor">
          <TextInput id={`date-${i}-aet`} labelText="AE title" hideLabel placeholder="AE title" value={d.aet}
            onChange={(e) => updateDate(i, { aet: e.target.value })} />
          <Select id={`date-${i}-mode`} labelText="Mode" hideLabel value={d.mode}
            onChange={(e) => updateDate(i, { mode: e.target.value as DateRestrictionInput['mode'] })}>
            <SelectItem value="today" text="today" />
            <SelectItem value="last_days" text="last_days" />
            <SelectItem value="date_range" text="date_range" />
          </Select>
          {d.mode === 'last_days' ? (
            <NumberInput id={`date-${i}-days`} label="days" hideLabel placeholder="days" value={d.days ?? ''}
              onChange={(_e, { value }: { value: number | string }) => updateDate(i, { days: value === '' ? null : Number(value) })} />
          ) : d.mode === 'date_range' ? (
            <>
              <TextInput id={`date-${i}-start`} labelText="Start date" hideLabel placeholder="YYYYMMDD" value={d.start_date ?? ''}
                onChange={(e) => updateDate(i, { start_date: e.target.value || null })} />
              <TextInput id={`date-${i}-end`} labelText="End date" hideLabel placeholder="YYYYMMDD" value={d.end_date ?? ''}
                onChange={(e) => updateDate(i, { end_date: e.target.value || null })} />
            </>
          ) : <span className="muted">today only</span>}
          <Button kind="ghost" size="sm" renderIcon={TrashIcon} hasIconOnly iconDescription="Remove"
            onClick={() => onDates(dates.filter((_, idx) => idx !== i))} />
        </div>
      ))}

      <div className="toolbar">
        <h3 className="section__subtitle">By label</h3>
        <Button kind="tertiary" size="sm" renderIcon={AddIcon}
          onClick={() => onLabels([...labels, { aet: '', labels: [], constraint: 'Any' }])}>
          Add
        </Button>
      </div>
      {labels.map((l, i) => (
        <div key={i} className="row-editor">
          <TextInput id={`label-${i}-aet`} labelText="AE title" hideLabel placeholder="AE title" value={l.aet}
            onChange={(e) => updateLabel(i, { aet: e.target.value })} />
          <TextInput id={`label-${i}-labels`} labelText="Labels" hideLabel placeholder="labels, comma-separated"
            value={l.labels.join(', ')}
            onChange={(e) => updateLabel(i, { labels: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
          <Select id={`label-${i}-constraint`} labelText="Constraint" hideLabel value={l.constraint}
            onChange={(e) => updateLabel(i, { constraint: e.target.value as LabelRestrictionInput['constraint'] })}>
            <SelectItem value="Any" text="Any" />
            <SelectItem value="All" text="All" />
          </Select>
          <Button kind="ghost" size="sm" renderIcon={TrashIcon} hasIconOnly iconDescription="Remove"
            onClick={() => onLabels(labels.filter((_, idx) => idx !== i))} />
        </div>
      ))}
    </Card>
  );
}

// ── peers ────────────────────────────────────────────────────────────────────

function PeerEditor({
  value,
  onChange,
}: {
  value: PeerInput[];
  onChange: (p: PeerInput[]) => void;
}) {
  function update(i: number, patch: Partial<PeerInput>) {
    onChange(value.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }
  // Kind is derived from which side is populated; switching clears the other.
  function setKind(i: number, kind: 'internal' | 'external') {
    const cur = value[i];
    if (!cur) return;
    update(
      i,
      kind === 'internal'
        ? { target_slug: cur.target_slug || '', url_env: null, username_env: null, password_env: null }
        : { target_slug: null, url_env: cur.url_env || '', username_env: null, password_env: null },
    );
  }
  return (
    <Card className="stack">
      <div className="toolbar">
        <h2 className="section__title">
          Peers <Badge tone="neutral">{value.length}</Badge>
        </h2>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={AddIcon}
          onClick={() =>
            onChange([
              ...value,
              { peer_name: '', target_slug: '', url_env: null, username_env: null, password_env: null, http_timeout: 60 },
            ])
          }
        >
          Add peer
        </Button>
      </div>
      <p className="field__help muted">
        Other Orthanc instances this center forwards to. An <strong>internal</strong> peer is
        another center in this fleet (by slug; no credentials — the shared network and disabled
        auth handle it). An <strong>external</strong> peer is a remote Orthanc whose URL and
        optional credentials are read at container start from <code>.env</code> variables you
        name here (never the secrets themselves).
      </p>
      {value.map((p, i) => {
        const kind: 'internal' | 'external' = p.url_env != null ? 'external' : 'internal';
        return (
          <div key={i} className="stack" style={{ borderTop: '1px solid var(--cds-border-subtle)', paddingTop: '0.75rem' }}>
            <div className="row-editor">
              <TextInput id={`peer-${i}-name`} labelText="Peer name" hideLabel placeholder="Peer name"
                value={p.peer_name} onChange={(e) => update(i, { peer_name: e.target.value })} />
              <Select id={`peer-${i}-kind`} labelText="Kind" hideLabel value={kind}
                onChange={(e) => setKind(i, e.target.value as 'internal' | 'external')}>
                <SelectItem value="internal" text="internal (another center)" />
                <SelectItem value="external" text="external (remote Orthanc)" />
              </Select>
              <NumberInput id={`peer-${i}-timeout`} label="HTTP timeout (s)" hideLabel min={1}
                value={p.http_timeout}
                onChange={(_e, { value: val }: { value: number | string }) => update(i, { http_timeout: Number(val) })} />
              <Button kind="ghost" size="sm" renderIcon={TrashIcon} hasIconOnly iconDescription="Remove"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))} />
            </div>
            {kind === 'internal' ? (
              <TextInput id={`peer-${i}-target`} labelText="Target center slug"
                helperText="The peer center's slug in this fleet, e.g. center12."
                value={p.target_slug ?? ''} onChange={(e) => update(i, { target_slug: e.target.value })} />
            ) : (
              <div className="row-editor">
                <TextInput id={`peer-${i}-urlenv`} labelText="URL env var" placeholder="CLOUD_ORTHANC_URL"
                  value={p.url_env ?? ''} onChange={(e) => update(i, { url_env: e.target.value })} />
                <TextInput id={`peer-${i}-userenv`} labelText="Username env var (optional)" placeholder="CLOUD_ORTHANC_USER"
                  value={p.username_env ?? ''} onChange={(e) => update(i, { username_env: e.target.value || null })} />
                <TextInput id={`peer-${i}-passenv`} labelText="Password env var (optional)" placeholder="CLOUD_ORTHANC_PASSWORD"
                  value={p.password_env ?? ''} onChange={(e) => update(i, { password_env: e.target.value || null })} />
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}
